import React from "react";
// The real marks, as the design team exported them. Each is the app's own
// logo, in the app's own colours — Meet's four-colour camera, Zoom's blue
// tile — which is the only way a row of them reads as "the apps you already
// have" rather than as two more of our icons.
import { ReactComponent as GoogleMeetMark } from "../../../assets/svg/appointment/google-meet.svg";
import { ReactComponent as ZoomMark } from "../../../assets/svg/appointment/zoom.svg";
import type { MeetingAppId } from "../meetingApps";

/**
 * The logo of an app a meeting link can come from, as that app draws it.
 *
 * These used to be traced by hand here, in flat approximations of the two
 * logos, because there were no files for them. There are now, so the tracings
 * are gone: somebody else's mark is not ours to redraw, and an approximation
 * of a logo is the one kind of icon that is wrong at every size.
 *
 * What this still owns is how big each one is allowed to look — see
 * `DRAWN_AT`.
 */
export interface MeetingAppMarkProps {
  app: MeetingAppId;
  /** The side of the square both marks are fitted to, in pixels. */
  size?: number;
  className?: string;
}

const GLYPHS: Record<MeetingAppId, React.FC<React.SVGProps<SVGSVGElement>>> = {
  "google-meet": GoogleMeetMark,
  zoom: ZoomMark,
};

/**
 * The square each file was exported at.
 *
 * They differ, and that difference is the design team balancing the two by
 * eye: Meet's camera sits inside its box with air around it, and Zoom's mark
 * is a solid tile that runs to its own edges. A filled tile and an open glyph
 * at the same measurement do not read as the same size, so Meet was drawn
 * larger to compensate.
 *
 * Which means the ratio between these two numbers is the balance, and the way
 * to keep it is to scale both against the larger rather than fit both to the
 * caller's square. The outer square stays the caller's either way, so a column
 * of marks still lines up on one edge rather than on whichever logo is widest.
 */
const DRAWN_AT: Record<MeetingAppId, number> = {
  "google-meet": 38,
  zoom: 28,
};

const LARGEST = Math.max(...Object.values(DRAWN_AT));

const MeetingAppMark: React.FC<MeetingAppMarkProps> = ({
  app,
  size = 32,
  className = "",
}) => {
  const Glyph = GLYPHS[app];
  const side = Math.round((size * DRAWN_AT[app]) / LARGEST);

  return (
    <span
      aria-hidden="true"
      style={{ width: size, height: size }}
      className={`inline-flex flex-shrink-0 items-center justify-center ${className}`}
    >
      {/* Sized in pixels rather than by a class: the number is worked out from
          the file's own export size, which no set of utility classes knows
          about. The gradients and the mask inside Meet's file carry ids of
          their own, so two of these on one page put the same id in the
          document twice — harmless, because both instances are identical and
          a `url(#id)` resolves to the first. */}
      <Glyph width={side} height={side} />
    </span>
  );
};

export default MeetingAppMark;
